import { createHash } from 'crypto'

import {
  QUICK_AI_MATURITY_BANDS,
  QUICK_AI_PRIMARY_ASSESSMENT_POLICY
} from '@/lib/quick-ai-readiness/config'
import {
  QuickAiEvidence,
  QuickAiBulkAssessmentScope,
  QuickAiLeadAccessState,
  QuickAiLeadInput,
  QuickAiLeadRecord,
  QuickAiMaturityBand,
  QuickAiScanRecord
} from '@/lib/quick-ai-readiness/types'

const COMMON_BUSINESS_SUFFIXES = [
  'inc',
  'llc',
  'ltd',
  'limited',
  'corp',
  'corporation',
  'co',
  'company'
]

export function normalizeWebsiteUrl(value: string): string {
  const trimmed = value.trim()
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  const url = new URL(withProtocol)
  url.hash = ''
  url.search = ''
  url.pathname = url.pathname === '/' ? '' : url.pathname.replace(/\/+$/, '')
  return url.toString().replace(/\/$/, '')
}

export function extractDomain(value: string): string {
  const normalized = normalizeWebsiteUrl(value)
  return new URL(normalized).hostname.replace(/^www\./, '').toLowerCase()
}

export function safeExtractDomain(value?: string | null): string | null {
  if (!value) {
    return null
  }

  try {
    return extractDomain(value)
  } catch {
    return null
  }
}

const MULTI_LABEL_SUFFIXES = new Set([
  'co.uk',
  'org.uk',
  'com.au',
  'net.au',
  'co.nz',
  'com.br',
  'co.in',
  'com.mx',
  'co.za'
])

/** Registrable domain (eTLD+1 approximation) for identity comparisons. */
export function registrableDomain(value?: string | null): string | null {
  if (!value) {
    return null
  }

  const host = (
    safeExtractDomain(value) ||
    value
      .toLowerCase()
      .replace(/^www\./, '')
      .split('/')[0]
  ).replace(/^www\./, '')
  const labels = host.split('.').filter(Boolean)
  if (labels.length < 2) {
    return host || null
  }

  const lastTwo = labels.slice(-2).join('.')
  if (MULTI_LABEL_SUFFIXES.has(lastTwo) && labels.length >= 3) {
    return labels.slice(-3).join('.')
  }

  return lastTwo
}

const DOMAIN_TOKEN_PATTERN = /\b(?:[a-z0-9-]+\.)+[a-z]{2,}\b/gi

/** Domain-looking tokens mentioned in a URL or snippet ("…/review/www.acme.com"). */
export function extractMentionedDomains(value?: string | null): string[] {
  if (!value) {
    return []
  }

  const matches = value.toLowerCase().match(DOMAIN_TOKEN_PATTERN) || []
  return [
    ...new Set(
      matches
        .map((match) => registrableDomain(match))
        .filter((domain): domain is string => Boolean(domain))
    )
  ]
}

/**
 * Guard against brand collisions: evidence that names the lead's brand on a
 * DIFFERENT registrable domain (marshmallow.com reviews attached to a
 * marshmallow.studio lead) is about another company, not this lead. Evidence
 * with no explicit subject domain is left alone — third-party coverage of
 * the business rarely repeats its domain.
 */
export function hasForeignBrandDomain(params: {
  leadDomain: string
  sourceUrl?: string | null
  text?: string | null
}): boolean {
  const leadRegistrable = registrableDomain(params.leadDomain)
  if (!leadRegistrable) {
    return false
  }

  const leadBrandLabel = leadRegistrable.split('.')[0]
  const mentioned = new Set<string>([
    ...extractMentionedDomains(params.sourceUrl),
    ...extractMentionedDomains(params.text)
  ])
  const sourceRegistrable = registrableDomain(params.sourceUrl)
  if (sourceRegistrable) {
    mentioned.add(sourceRegistrable)
  }

  for (const domain of mentioned) {
    if (domain === leadRegistrable) {
      continue
    }
    // Same brand label, different domain: a namesake company.
    if (domain.split('.')[0] === leadBrandLabel) {
      return true
    }
  }

  // Review/aggregator URLs embed the reviewed domain in the path
  // (trustpilot.com/review/<domain>). A foreign domain there is decisive.
  if (params.sourceUrl) {
    try {
      const pathDomains = extractMentionedDomains(
        new URL(params.sourceUrl).pathname
      )
      if (
        pathDomains.length &&
        !pathDomains.includes(leadRegistrable) &&
        sourceRegistrable !== leadRegistrable
      ) {
        return true
      }
    } catch {
      // Ignore malformed URLs; other guards still apply.
    }
  }

  return false
}

export function normalizeBusinessName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !COMMON_BUSINESS_SUFFIXES.includes(token))
    .join(' ')
}

export function toTitleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function buildLeadDedupeKey(
  input: Pick<QuickAiLeadInput, 'businessName' | 'website'>
): string {
  const normalizedName = normalizeBusinessName(input.businessName)
  const domain = extractDomain(input.website)
  return `${domain}::${normalizedName}`
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function clamp(value: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, value))
}

export function roundTo(value: number, precision = 2): number {
  const multiplier = 10 ** precision
  return Math.round(value * multiplier) / multiplier
}

export function scoreToBand(score: number): QuickAiMaturityBand {
  const match = QUICK_AI_MATURITY_BANDS.find((band) => score >= band.min && score <= band.max)
  return match?.band ?? 'high_opportunity'
}

export function labelForBand(band: QuickAiMaturityBand): string {
  return QUICK_AI_MATURITY_BANDS.find((item) => item.band === band)?.label ?? 'High Opportunity'
}

export function hashText(value: string): string {
  return createHash('sha256').update(value).digest('hex')
}

export function truncateText(value: string, maxLength = 220): string {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength - 1).trim()}…`
}

export function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return [
    ...new Set(values.filter((value): value is string => !!value).map((value) => value.trim()))
  ]
}

export function formatLocationLabel(input: {
  city?: string | null
  stateProvince?: string | null
  country?: string | null
}): string {
  return [input.city, input.stateProvince, input.country].filter(Boolean).join(', ')
}

export function buildRegionKey(input: {
  city?: string | null
  stateProvince?: string | null
  country?: string | null
}): string {
  return [input.city, input.stateProvince, input.country]
    .filter(Boolean)
    .map((value) => slugify(value || ''))
    .filter(Boolean)
    .join('::')
}

export function createEvidence(label: string, snippet: string, sourceUrl: string): QuickAiEvidence {
  return {
    label,
    snippet: truncateText(snippet),
    sourceUrl
  }
}

export function summarizeEvidence(evidence: QuickAiEvidence[], maxItems = 3): string[] {
  return evidence.slice(0, maxItems).map((item) => `${item.label}: ${item.snippet}`)
}

export function average(values: number[]): number {
  if (!values.length) {
    return 0
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length
}

export function percentage(part: number, total: number): number {
  if (!total) {
    return 0
  }

  return roundTo((part / total) * 100, 2)
}

export function hoursSince(timestamp?: string | null): number {
  if (!timestamp) {
    return Number.POSITIVE_INFINITY
  }

  const value = new Date(timestamp).getTime()
  if (Number.isNaN(value)) {
    return Number.POSITIVE_INFINITY
  }

  return (Date.now() - value) / (1000 * 60 * 60)
}

export function isLeadAccessed(lead: Pick<QuickAiLeadRecord, 'firstAccessedAt'>): boolean {
  return Boolean(lead.firstAccessedAt)
}

export function getLeadAccessState(
  lead: Pick<QuickAiLeadRecord, 'firstAccessedAt'>
): QuickAiLeadAccessState {
  return isLeadAccessed(lead) ? 'accessed' : 'unaccessed'
}

export function countAccessedLeads(
  leads: Array<Pick<QuickAiLeadRecord, 'firstAccessedAt'>>
): number {
  return leads.filter(isLeadAccessed).length
}

export function countUnaccessedLeads(
  leads: Array<Pick<QuickAiLeadRecord, 'firstAccessedAt'>>
): number {
  return leads.length - countAccessedLeads(leads)
}

export function getUnaccessedLeadIds(
  leads: Array<Pick<QuickAiLeadRecord, 'id' | 'firstAccessedAt'>>
): string[] {
  return leads.filter((lead) => !isLeadAccessed(lead)).map((lead) => lead.id)
}

export function getScanPageOffset(
  scan: Pick<QuickAiScanRecord, 'rawIngestionSummary'> | null | undefined
): number {
  const rawValue = scan?.rawIngestionSummary?.pageOffset
  const value =
    typeof rawValue === 'number' ? rawValue : typeof rawValue === 'string' ? Number(rawValue) : 0

  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

export function isPrimaryAssessmentScan(
  scan: Pick<QuickAiScanRecord, 'rawIngestionSummary'> | null | undefined
): boolean {
  if (scan?.rawIngestionSummary?.useAsPrimaryAssessment === false) {
    return false
  }

  const scanMode =
    typeof scan?.rawIngestionSummary?.scanMode === 'string'
      ? scan.rawIngestionSummary.scanMode
      : null

  return scanMode !== 'background' || getScanPageOffset(scan) === 0
}

function toNumberOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function sortScansNewestFirst(scans: QuickAiScanRecord[]): QuickAiScanRecord[] {
  return [...scans].sort((left, right) => {
    const createdDelta = new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    if (createdDelta !== 0) {
      return createdDelta
    }

    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
  })
}

export function shouldSkipRetroactivePrimaryAssessmentScan(
  current: QuickAiScanRecord,
  previousPrimary?: QuickAiScanRecord | null
): boolean {
  if (!previousPrimary) {
    return false
  }

  const raw = (current.rawIngestionSummary || {}) as Record<string, any>
  const observed = (raw.observed || {}) as Record<string, any>
  const diagnostics = (observed.crawlDiagnostics || {}) as Record<string, any>
  const crawlSummary = (raw.crawlSummary || {}) as Record<string, any>
  const completeness = toNumberOrZero(diagnostics.scanCompletenessScore)
  const pagesFetched = toNumberOrZero(diagnostics.pagesFetched || observed.pagesFetched)
  const failedUrlCount = Array.isArray(crawlSummary.failedUrls) ? crawlSummary.failedUrls.length : 0
  const blockedUrlCount = Array.isArray(crawlSummary.blockedUrls)
    ? crawlSummary.blockedUrls.length
    : 0
  const previousScore = toNumberOrZero(previousPrimary.overallScore)
  const previousConfidence = toNumberOrZero(previousPrimary.confidenceScore)
  const scoreDrop = previousScore - toNumberOrZero(current.overallScore)
  const confidenceDrop = previousConfidence - toNumberOrZero(current.confidenceScore)

  const limitedCoverage =
    completeness < QUICK_AI_PRIMARY_ASSESSMENT_POLICY.minCompleteness ||
    pagesFetched < QUICK_AI_PRIMARY_ASSESSMENT_POLICY.minPagesFetched
  const degradedScan =
    current.status === 'partial' || failedUrlCount > 0 || blockedUrlCount > 0 || limitedCoverage
  const significantDrop =
    scoreDrop >= QUICK_AI_PRIMARY_ASSESSMENT_POLICY.maxScoreDrop ||
    confidenceDrop >= QUICK_AI_PRIMARY_ASSESSMENT_POLICY.maxConfidenceDrop

  return degradedScan && significantDrop
}

export function selectPrimaryAssessmentScan(
  scans: QuickAiScanRecord[] | null | undefined
): QuickAiScanRecord | null {
  const completedScans = sortScansNewestFirst(
    (scans || []).filter((scan) => scan.status === 'completed' || scan.status === 'partial')
  )
  const primaryCandidates = completedScans.filter((scan) => isPrimaryAssessmentScan(scan))

  for (let index = 0; index < primaryCandidates.length; index += 1) {
    const current = primaryCandidates[index]
    const previousPrimary = primaryCandidates[index + 1] || null

    if (shouldSkipRetroactivePrimaryAssessmentScan(current, previousPrimary)) {
      continue
    }

    return current
  }

  return primaryCandidates[0] || completedScans[0] || null
}

export function applyAssessmentScanToLead(
  lead: QuickAiLeadRecord,
  assessmentScan: QuickAiScanRecord | null | undefined
): QuickAiLeadRecord {
  if (!assessmentScan) {
    return lead
  }

  return {
    ...lead,
    latestAssessmentScore: assessmentScan.overallScore ?? null,
    latestSalesPriorityScore: assessmentScan.salesPriorityScore ?? null,
    latestConfidenceScore: assessmentScan.confidenceScore ?? null,
    lastScanId: assessmentScan.id,
    lastScanAt:
      assessmentScan.completedAt || assessmentScan.startedAt || assessmentScan.createdAt || null,
    leadStatus:
      lead.leadStatus === 'researching' || lead.leadStatus === 'new' ? 'assessed' : lead.leadStatus
  }
}

export function isLeadAssessmentSummaryStale(
  lead: QuickAiLeadRecord,
  assessmentScan: QuickAiScanRecord | null | undefined
): boolean {
  if (!assessmentScan) {
    return false
  }

  const assessmentTimestamp =
    assessmentScan.completedAt || assessmentScan.startedAt || assessmentScan.createdAt || null

  return (
    lead.lastScanId !== assessmentScan.id ||
    (lead.latestAssessmentScore ?? null) !== (assessmentScan.overallScore ?? null) ||
    (lead.latestSalesPriorityScore ?? null) !== (assessmentScan.salesPriorityScore ?? null) ||
    (lead.latestConfidenceScore ?? null) !== (assessmentScan.confidenceScore ?? null) ||
    (lead.lastScanAt ?? null) !== assessmentTimestamp
  )
}

export function selectBulkAssessmentLeads(params: {
  leads: QuickAiLeadRecord[]
  scope: QuickAiBulkAssessmentScope
  leadIds?: string[]
}): QuickAiLeadRecord[] {
  const selectedLeadIds = new Set(params.leadIds || [])

  return params.leads.filter((lead) => {
    if (lead.leadKind !== 'prospect') {
      return false
    }

    if (params.scope === 'all_unaccessed') {
      return !lead.firstAccessedAt
    }

    if (params.scope === 'all_unassessed') {
      return typeof lead.latestAssessmentScore !== 'number'
    }

    return selectedLeadIds.has(lead.id)
  })
}

export function getNextUnaccessedLeadId(
  leads: Array<Pick<QuickAiLeadRecord, 'id' | 'firstAccessedAt'>>,
  currentLeadId?: string | null
): string | null {
  return leads.find((lead) => !isLeadAccessed(lead) && lead.id !== currentLeadId)?.id || null
}
